import { PrintReportModalTOCItem, TaxReturnTOCItemWithRequiredNodeKey } from '../data-types/printReportModalTOCItem';
import { IWhitePaperColumn, TaxReturnTOC } from '../model';
import { buildTocItemsFromTOC, getInitiallyExpandedNodeKeys, getInitialSelectedNodeKeys } from '../utils/PrintReportModalStateUtils';

export interface PrintReportModalState {
    // leaf node keys that are selected
    selectedNodeKeys: Set<string>;
    // node keys that are expanded/open
    openNodeKeys: Set<string>;
    // current search text
    searchText: string;
    // whether the reducer has been explicitly seeded with computed initial state
    isSeeded: boolean;
    // modal visibility flag
    isOpen: boolean;
    // currently selected entity for the modal
    selectedEntity: IWhitePaperColumn | undefined;
    // current TOC data for the modal
    tocData: TaxReturnTOC | null | undefined;
    // fetching state for TOC requests
    isFetchingToc: boolean;
    // TOC fetch error flag
    hasTocFetchError: boolean;
    // whether to print blank forms or not
    doNotPrintBlankForms: boolean;
}

export type PrintReportModalAction =
    | { type: 'modal-opened'; selectedEntity: IWhitePaperColumn | undefined }
    | {
          type: 'modal-opened-overflow';
          selectedEntity: IWhitePaperColumn | undefined;
          tocData: TaxReturnTOC | null | undefined;
          initialSelectedTocItems?: TaxReturnTOCItemWithRequiredNodeKey[];
      }
    | { type: 'entity-state-changed'; selectedEntity: IWhitePaperColumn | undefined }
    | { type: 'toc-fetch-start' }
    | {
          type: 'toc-fetch-success';
          tocData: TaxReturnTOC | null | undefined;
          initialSelectedTocItems?: TaxReturnTOCItemWithRequiredNodeKey[];
      }
    | { type: 'toc-fetch-failure' }
    | { type: 'toc-fetch-finished' }
    | { type: 'checkbox-check-state-changed'; nodeId: string; checked: boolean | 'mixed'; leafNodeKeys: string[]; shouldSelect: boolean }
    | { type: 'node-expansion-state-changed'; nextOpenNodeIds: Set<string> }
    | { type: 'modal-closed' }
    | { type: 'confirm-click' }
    | { type: 'cancel-click' }
    | { type: 'search-state-change'; searchText: string; openNodeKeys: Set<string> }
    | { type: 'do-not-print-blank-forms-check-state-changed'; checked: boolean };

export const initialPrintReportModalState: PrintReportModalState = {
    selectedNodeKeys: new Set<string>(),
    openNodeKeys: new Set<string>(),
    searchText: '',
    isSeeded: false,
    isOpen: false,
    selectedEntity: undefined,
    tocData: undefined,
    isFetchingToc: false,
    hasTocFetchError: false,
    doNotPrintBlankForms: false,
};

const buildSeededSelectionState = (
    tocData: TaxReturnTOC | null | undefined,
    initialSelectedTocItems?: TaxReturnTOCItemWithRequiredNodeKey[]
): { selectedNodeKeys: Set<string>; openNodeKeys: Set<string> } => {
    const tocItems: PrintReportModalTOCItem[] = buildTocItemsFromTOC(tocData);
    const initSelected: Set<string> = getInitialSelectedNodeKeys(tocItems, initialSelectedTocItems);
    const initOpen: Set<string> = getInitiallyExpandedNodeKeys(tocItems, initSelected);

    return {
        selectedNodeKeys: initSelected,
        openNodeKeys: initOpen,
    };
};

export const printReportModalStateReducer = (state: PrintReportModalState, action: PrintReportModalAction): PrintReportModalState => {
    switch (action.type) {
        case 'modal-opened':
            return {
                ...state,
                isOpen: true,
                selectedEntity: action.selectedEntity,
                tocData: undefined,
                selectedNodeKeys: new Set<string>(),
                openNodeKeys: new Set<string>(),
                searchText: '',
                isSeeded: false,
                isFetchingToc: false,
                hasTocFetchError: false,
            };

        case 'modal-opened-overflow': {
            const seededState: { selectedNodeKeys: Set<string>; openNodeKeys: Set<string> } = buildSeededSelectionState(
                action.tocData,
                action.initialSelectedTocItems
            );

            return {
                ...state,
                isOpen: true,
                selectedEntity: action.selectedEntity,
                tocData: action.tocData,
                selectedNodeKeys: seededState.selectedNodeKeys,
                openNodeKeys: seededState.openNodeKeys,
                searchText: '',
                isSeeded: true,
                isFetchingToc: false,
                hasTocFetchError: false,
            };
        }

        case 'entity-state-changed': {
            return {
                ...state,
                selectedEntity: action.selectedEntity,
                tocData: undefined,
                selectedNodeKeys: new Set<string>(),
                openNodeKeys: new Set<string>(),
                searchText: '',
                isSeeded: false,
                isFetchingToc: false,
                hasTocFetchError: false,
            };
        }

        case 'toc-fetch-start':
            return {
                ...state,
                isFetchingToc: true,
                hasTocFetchError: false,
            };

        case 'toc-fetch-success': {
            const seededState: { selectedNodeKeys: Set<string>; openNodeKeys: Set<string> } = buildSeededSelectionState(
                action.tocData,
                action.initialSelectedTocItems
            );

            return {
                ...state,
                tocData: action.tocData,
                selectedNodeKeys: seededState.selectedNodeKeys,
                openNodeKeys: seededState.openNodeKeys,
                searchText: '',
                isSeeded: true,
                hasTocFetchError: false,
            };
        }

        case 'toc-fetch-failure':
            return {
                ...state,
                hasTocFetchError: true,
            };

        case 'toc-fetch-finished':
            return {
                ...state,
                isFetchingToc: false,
            };

        case 'checkbox-check-state-changed': {
            const { leafNodeKeys, shouldSelect } = action;
            const nextSelected: Set<string> = new Set<string>(state.selectedNodeKeys);
            for (const k of leafNodeKeys) {
                if (shouldSelect) nextSelected.add(k);
                else nextSelected.delete(k);
            }
            return { ...state, selectedNodeKeys: nextSelected, isSeeded: true };
        }

        case 'node-expansion-state-changed': {
            return { ...state, openNodeKeys: action.nextOpenNodeIds, isSeeded: true };
        }

        case 'search-state-change': {
            return { ...state, searchText: action.searchText, openNodeKeys: action.openNodeKeys, isSeeded: true };
        }

        case 'modal-closed': {
            return {
                selectedNodeKeys: new Set<string>(),
                openNodeKeys: new Set<string>(),
                searchText: '',
                isSeeded: false,
                isOpen: false,
                selectedEntity: undefined,
                tocData: undefined,
                isFetchingToc: false,
                hasTocFetchError: false,
                doNotPrintBlankForms: false,
            };
        }

        case 'confirm-click':
        case 'cancel-click':
            return state;

        case 'do-not-print-blank-forms-check-state-changed': {
            return {
                ...state,
                doNotPrintBlankForms: action.checked,
            };
        }

        default:
            return state;
    }
};

export default printReportModalStateReducer;
